/**
 * Fixed zoom levels of clusters for icons drawn at a fixed size in pixels (the 2D plan icons).
 *
 * All levels are built once from the points. Zooming only picks a level, so nothing is
 * re-clustered on a zoom tick. Level L groups points within `frameSize / 2^L` of each other,
 * where `frameSize` is the larger side of the points' bounding box. The finest level is
 * `maxLevel`; `maxLevel + 1` is every point on its own.
 *
 * Each level is built from the next finer one, so zooming in only ever splits a group and
 * never merges groups that were apart. Points are visited in sorted (x, y, key) order, so
 * the groups depend on where the points are and not on the order they arrive in.
 */

/**
 * @template T
 * @typedef {Object} ClusterLevels
 * @property {T[]} items       Input items.
 * @property {number} frameSize Larger side of the points' bounding box (1 when all points share a spot).
 * @property {number} radiusPx  Minimum on-screen radius of a group (see clusterLevelFor).
 * @property {number} maxLevel  Finest clustered level; maxLevel + 1 is every point alone.
 * @property {Array<Array<{ x: number, y: number, leaves: number[] }>>} levels Nodes per level.
 */

/**
 * @template T
 * @param {T[]} items
 * @param {{ getPoint: (item: T) => { x: number, y: number }, getKey: (item: T) => string|number,
 *           radiusPx: number, maxLevel: number }} options
 * @returns {ClusterLevels<T>|null} null when there are no items.
 */
export function buildClusterLevels(items, { getPoint, getKey, radiusPx, maxLevel }) {
  if (!items?.length) return null;

  const points = items.map((item, index) => {
    const { x, y } = getPoint(item);
    return { x, y, key: String(getKey(item)), index };
  });
  points.sort((a, b) => a.x - b.x || a.y - b.y || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));

  const frameSize = boundingSize(points) || 1;
  const levels = new Array(maxLevel + 2);
  levels[maxLevel + 1] = points.map((p) => ({ x: p.x, y: p.y, leaves: [p.index] }));

  for (let level = maxLevel; level >= 0; level--) {
    levels[level] = mergeWithin(levels[level + 1], frameSize / 2 ** level);
  }

  return { items, frameSize, radiusPx, maxLevel, levels };
}

/**
 * The level to draw at a zoom: the on-screen radius of a group stays in [radiusPx, 2 * radiusPx),
 * so icons of that size do not overlap.
 * @param {ClusterLevels<unknown>|null} clusterLevels
 * @param {number} pxPerUnit Screen pixels per viewer unit at the current zoom.
 * @returns {number} 0..maxLevel + 1
 */
export function clusterLevelFor(clusterLevels, pxPerUnit) {
  if (!clusterLevels || !(pxPerUnit > 0)) return 0;
  const level = Math.floor(Math.log2((clusterLevels.frameSize * pxPerUnit) / clusterLevels.radiusPx));
  return Math.min(Math.max(level, 0), clusterLevels.maxLevel + 1);
}

/**
 * @template T
 * @param {ClusterLevels<T>|null} clusterLevels
 * @param {number} level
 * @returns {{ clusters: T[][], singles: T[] }}
 */
export function clustersAtLevel(clusterLevels, level) {
  if (!clusterLevels) return { clusters: [], singles: [] };
  const { items, levels, maxLevel } = clusterLevels;
  const nodes = levels[Math.min(Math.max(level, 0), maxLevel + 1)];
  const clusters = [];
  const singles = [];
  nodes.forEach((node) => {
    if (node.leaves.length > 1) clusters.push(node.leaves.map((i) => items[i]));
    else singles.push(items[node.leaves[0]]);
  });
  return { clusters, singles };
}

function boundingSize(points) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  points.forEach(({ x, y }) => {
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  });
  return Math.max(maxX - minX, maxY - minY);
}

/**
 * One greedy pass: each node not yet taken absorbs every untaken node within `radius` of it, and
 * the group sits at the leaf-weighted centre. Neighbours are found through a grid of `radius` cells.
 */
function mergeWithin(nodes, radius) {
  const grid = new Map();
  const cellOf = (v) => Math.floor(v / radius);
  nodes.forEach((node, i) => {
    const key = `${cellOf(node.x)},${cellOf(node.y)}`;
    const cell = grid.get(key);
    if (cell) cell.push(i);
    else grid.set(key, [i]);
  });

  const taken = new Uint8Array(nodes.length);
  const merged = [];
  const radius2 = radius * radius;

  nodes.forEach((node, i) => {
    if (taken[i]) return;
    taken[i] = 1;

    const group = [node];
    const cx = cellOf(node.x);
    const cy = cellOf(node.y);
    for (let gx = cx - 1; gx <= cx + 1; gx++) {
      for (let gy = cy - 1; gy <= cy + 1; gy++) {
        const cell = grid.get(`${gx},${gy}`);
        if (!cell) continue;
        cell.forEach((j) => {
          if (taken[j]) return;
          const dx = nodes[j].x - node.x;
          const dy = nodes[j].y - node.y;
          if (dx * dx + dy * dy <= radius2) {
            taken[j] = 1;
            group.push(nodes[j]);
          }
        });
      }
    }

    if (group.length === 1) {
      merged.push(node);
      return;
    }
    let wx = 0, wy = 0, count = 0;
    const leaves = [];
    group.forEach((g) => {
      wx += g.x * g.leaves.length;
      wy += g.y * g.leaves.length;
      count += g.leaves.length;
      leaves.push(...g.leaves);
    });
    merged.push({ x: wx / count, y: wy / count, leaves });
  });

  return merged;
}
